'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { apply as applyVisualEditing, setAttr } from '@directus/visual-editing';

interface ApplyOptions {
	elements?: HTMLElement[] | HTMLElement;
	onSaved?: () => void;
	mode?: 'modal' | 'popover' | 'drawer';
	customClass?: string;
}

export function useVisualEditing() {
	const [isVisualEditingEnabled, setIsVisualEditingEnabled] = useState(false);
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();

	const enableVisualEditingEnv = process.env.NEXT_PUBLIC_ENABLE_VISUAL_EDITING === 'true';
	const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL || '';

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const param = searchParams.get('visual-editing');
		const isPreview = searchParams.get('preview') === 'true';

		// If the query param is present, respect it
		if (param === 'true' || isPreview) {
			if (enableVisualEditingEnv) {
				localStorage.setItem('visual-editing', 'true');
				setIsVisualEditingEnabled(true);
			} else {
				console.warn('Visual editing is not enabled in this environment.');
			}
		} else if (param === 'false') {
			localStorage.removeItem('visual-editing');
			setIsVisualEditingEnabled(false);
		} else {
			// Fallback to local storage
			const persisted = localStorage.getItem('visual-editing') === 'true';
			setIsVisualEditingEnabled(persisted);
		}

		// Cleanup URL if needed (but keep it if we are in an iframe/directus preview context so reload works)
		// We actually DON'T want to clean up the URL if we are inside Directus, as it relies on these params.
	}, [searchParams, enableVisualEditingEnv]);

	const apply = (options: Pick<ApplyOptions, 'elements' | 'onSaved' | 'mode' | 'customClass'>) => {
		// Only apply if we have a window and visual editing is enabled
		if (typeof window === 'undefined') return;

		// We check actual state or local storage to be sure to catch early init
		const enabled = isVisualEditingEnabled || localStorage.getItem('visual-editing') === 'true';

		if (!enabled) return;

		applyVisualEditing({
			...options,
			directusUrl,
		});
	};

	return {
		isVisualEditingEnabled,
		apply,
		setAttr,
	};
}
